import { Badge, Button, Card, CardHeader, EmptyState, SegmentedTabs, useToast } from "@helmr/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { isApiError } from "../../api/client";
import { assignRole, getStaff, removeStaff, updateStaff, type StaffMember } from "../../api/staff";
import { useServices } from "../../app/services";
import { Confirm, Failure, Field, FormError, messageOf, RowsSkeleton, styles as c, useScope } from "../classes/common";
import { StaffAttendanceTab } from "./StaffAttendanceTab";
import { ScheduleTab } from "./ScheduleTab";
import { StaffDocumentsSection } from "./StaffDocuments";
import { useRoleNames } from "./roles";

type Tab = "profile" | "schedule" | "attendance";

export function StaffProfile() {
  const { id = "" } = useParams();
  const { client } = useServices();
  const sc = useScope();
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get("tab") === "schedule" ? "schedule" : params.get("tab") === "attendance" ? "attendance" : "profile";
  const q = useQuery({ queryKey: ["staff", ...sc.key, "one", id], queryFn: () => getStaff(client, id), retry: false });
  if (q.isPending) return <Card aria-label="Staff profile"><RowsSkeleton rows={4} /></Card>;
  if (q.error) {
    if (isApiError(q.error) && q.error.status === 404) return <Card><EmptyState icon="alert" title="Staff not found" description="They may have been removed, or they work for another gym." action={<Button as={Link} to="/staff">Back to staff</Button>} /></Card>;
    return <Failure error={q.error} title="Could not load this person" onRetry={() => void q.refetch()} />;
  }
  const { staff, schedule } = q.data;
  return (
    <div className={c.page}>
      <div className={c.head}>
        <div><h1>{staff.name}</h1><p>{staff.status === "active" ? "Active" : "Removed"}</p></div>
        <div className={c.tools}>
          <Button as={Link} to="/staff">All staff</Button>
          <SegmentedTabs label="Staff sections" value={tab} onChange={(v) => setParams(v === "profile" ? {} : { tab: v })} options={[{ value: "profile", label: "Profile" }, { value: "schedule", label: "Schedule" }, { value: "attendance", label: "Attendance" }]} />
        </div>
      </div>
      {tab === "profile" ? <ProfileTab staff={staff} /> : tab === "schedule" ? <ScheduleTab staff={staff} schedule={schedule} /> : <StaffAttendanceTab staff={staff} />}
    </div>
  );
}

function ProfileTab({ staff }: { staff: StaffMember }) {
  const { client } = useServices();
  const sc = useScope();
  const qc = useQueryClient();
  const nav = useNavigate();
  const toast = useToast();
  const roles = useRoleNames();
  const [name, setName] = useState(staff.name);
  const [branch, setBranch] = useState(staff.homeBranchId ?? "");
  const [roleId, setRoleId] = useState(staff.roleId);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { setName(staff.name); setBranch(staff.homeBranchId ?? ""); setRoleId(staff.roleId); }, [staff.id, staff.name, staff.homeBranchId, staff.roleId]);
  const canEdit = sc.ctx.has("staff.edit_profile");
  // POST /staff/:id/role checks `roles.roles_assign` only (D57: no longer staff edit + roles edit).
  const canRole = sc.ctx.has("roles.roles_assign");
  const branches = sc.ctx.me?.branches ?? [];
  const done = (m: string) => { void qc.invalidateQueries({ queryKey: ["staff"] }); setError(null); toast.show(m); };
  const save = useMutation({ mutationFn: () => updateStaff(client, staff.id, { name: name.trim(), ...(branch && branch !== staff.homeBranchId ? { homeBranchId: branch } : {}) }), onSuccess: () => done("Profile saved"), onError: (e) => setError(messageOf(e)) });
  const role = useMutation({ mutationFn: () => assignRole(client, staff.id, roleId), onSuccess: (r) => done(r.changed ? "Role changed" : "Role unchanged"), onError: (e) => setError(messageOf(e)) });
  const remove = useMutation({ mutationFn: () => removeStaff(client, staff.id), onSuccess: () => { done("Staff removed"); nav("/staff"); }, onError: (e) => setError(messageOf(e)) });
  const roleName = roles.nameOf(staff.roleId);
  return (
    <>
      <FormError message={error} />
      <Card aria-label="Profile">
        <CardHeader title="Profile" subtitle="Pay is not shown here: Payroll has it." />
        <form className={`${c.pad} ${c.stack}`} onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
          <Field label="Name" htmlFor="sp-name"><input id="sp-name" className={c.input} value={name} maxLength={120} readOnly={!canEdit} onChange={(e) => setName(e.target.value)} /></Field>
          <Field label="Phone" htmlFor="sp-phone"><input id="sp-phone" className={c.input} value={staff.phone} readOnly /></Field>
          <Field label="Home branch" htmlFor="sp-branch">
            <select id="sp-branch" className={c.input} value={branch} disabled={!canEdit} onChange={(e) => setBranch(e.target.value)}>{!branch ? <option value="">No home branch</option> : null}{branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}</select>
          </Field>
          {canEdit ? <div><Button variant="primary" type="submit" busy={save.isPending}>Save profile</Button></div> : null}
        </form>
      </Card>
      <StaffDocumentsSection staff={staff} />
      <Card aria-label="Role">
        <CardHeader title="Role" />
        <div className={`${c.pad} ${c.stack}`}>
          {roles.canPick && canRole ? (
            <>
              <Field label="Role" htmlFor="sp-role" hint="You can give a role only if you hold everything it allows.">
                <select id="sp-role" className={c.input} value={roleId} onChange={(e) => setRoleId(e.target.value)}>{roles.roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</select>
              </Field>
              <div><Button variant="primary" busy={role.isPending} disabled={roleId === staff.roleId} onClick={() => role.mutate()}>Change role</Button></div>
            </>
          ) : (
            <div className={c.row}><Badge>{roleName ?? "Role not shown"}</Badge><span className={c.note}>{!roles.canPick ? "Changing a role needs access to Roles." : canRole ? "" : "Changing a role needs the Assign roles lever."}</span></div>
          )}
        </div>
      </Card>
      {sc.ctx.has("staff.deactivate") && staff.status === "active" ? (
        <Card aria-label="Remove">
          <CardHeader title="Remove from staff" />
          <div className={`${c.pad} ${c.stack}`}>
            <p className={c.note}>Removing marks them inactive and ends their sign-in. Punches and payroll history are kept. Booked PT sessions and classes are not moved automatically: reassign them first.</p>
            <Confirm label="Remove staff" question={`Remove ${staff.name}?`} busy={remove.isPending} onConfirm={() => remove.mutate()} />
          </div>
        </Card>
      ) : null}
    </>
  );
}
